import type { Node as PMNode } from '@tiptap/pm/model'

import { formatFieldValue, toNumber } from '@/lib/cube-report'
import { changePercent, EMPTY_VALUE, formatChange } from '@/lib/format'
import type { ReportRow } from '@/services/cube/report'

import type { DocumentDataSource, NamedResult } from './data-source'

/*
 * `value` / `change` / `condition` başvurularının çözümü (contracts.md §12): saf fonksiyonlar,
 * canlı ve donmuş kaynak aynısını kullanır. Boş değer kuralı: `null` -> "—", koşulda `else`.
 */

const RESERVED = ['period', 'filters']

export interface ParsedRef {
  /** `prev.sorgu.ölçü`: önceki dönem satırı. */
  prev: boolean
  /** `sorgu` adı, ya da `row` / `period` / `filters`. */
  head: string
  member: string
}

export function parseRef(ref: string): ParsedRef {
  const parts = ref.split('.')
  const prev = parts[0] === 'prev' && parts.length === 3
  if (prev) parts.shift()
  return { prev, head: parts[0] ?? '', member: parts[1] ?? '' }
}

/** `period.label` ve `filters.<üye>` sorguya değil bağlama başvurur. */
export const isContextRef = (ref: string) => RESERVED.includes(parseRef(ref).head)

/** Başvurunun okunacağı adlandırılmış sorgu (`row.` -> `repeat`'in `over`'ı); bağlam başvurusunda tanımsız. */
export function refQueryName(ref: string, repeatOver: string | undefined): string | undefined {
  const { head } = parseRef(ref)
  if (RESERVED.includes(head)) return undefined
  return head === 'row' ? repeatOver : head
}

function cell(
  result: NamedResult | undefined,
  member: string,
  rows: ReportRow[] | undefined,
): unknown {
  return result && rows?.[0]?.[`${result.view}.${member}`]
}

/** Düğümün gösterdiği metin. `row`: tekrar satırı (verilmezse sorgunun ilk satırı). */
export function valueText(
  ref: string,
  source: Pick<DocumentDataSource, 'periodLabel' | 'filterText'>,
  result: NamedResult | undefined,
  row?: ReportRow,
): string {
  const { head, member } = parseRef(ref)
  if (head === 'period') return source.periodLabel ?? EMPTY_VALUE
  if (head === 'filters') return source.filterText(member)
  if (!result) return EMPTY_VALUE
  const current = row ?? result.rows?.[0]
  const value = current?.[`${result.view}.${member}`]
  if (value === null || value === undefined || value === '') return EMPTY_VALUE
  const field = result.fields.get(`${result.view}.${member}`)
  return field ? formatFieldValue(field, value, current) : String(value)
}

/** `change`: önceki döneme göre yüzde; önceki yoksa ya da 0 ise "—". */
export function changeValueText(ref: string, result: NamedResult | undefined): string {
  const { member } = parseRef(ref)
  const percent = changePercent(
    toNumber(cell(result, member, result?.rows)),
    toNumber(cell(result, member, result?.previousRows)),
  )
  return percent === undefined ? EMPTY_VALUE : formatChange(percent)
}

export type ConditionOp = 'gt' | 'gte' | 'lt' | 'lte' | 'eq'

export interface ConditionAttrs {
  left: string
  op: ConditionOp
  right: string | number
}

const COMPARE: Record<ConditionOp, (a: number, b: number) => boolean> = {
  gt: (a, b) => a > b,
  gte: (a, b) => a >= b,
  lt: (a, b) => a < b,
  lte: (a, b) => a <= b,
  eq: (a, b) => a === b,
}

/**
 * `true` = `then` dalı. Sol ya da sağ taraf boşsa (`null`, satır yok) `false` -> `else`.
 * `leftResult` / `rightResult`: başvuruların sorgu sonuçları (aynı sorgu olabilir).
 */
export function evaluateCondition(
  { left, op, right }: ConditionAttrs,
  leftResult: NamedResult | undefined,
  rightResult: NamedResult | undefined,
): boolean {
  const a = toNumber(cell(leftResult, parseRef(left).member, leftResult?.rows))
  let b: number | null
  if (typeof right === 'number') b = right
  else {
    const ref = parseRef(right)
    b = toNumber(
      cell(rightResult, ref.member, ref.prev ? rightResult?.previousRows : rightResult?.rows),
    )
  }
  return a !== null && b !== null && COMPARE[op](a, b)
}

/** `pos`taki düğümün atalarında `repeat` varsa onun `over`'ı: `row.ölçü` başvuruları buna bakar. */
// ponytail: atadaki `over` sonradan değişirse alt düğüm görünümleri bir sonraki güncellemeye kadar eski sorguda kalır.
export function repeatOverAt(doc: PMNode, pos: number | undefined): string | undefined {
  if (pos === undefined) return undefined
  const $pos = doc.resolve(pos)
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth)
    if (node.type.name === 'repeat') return node.attrs.over as string
  }
  return undefined
}
